/**
 * クライアントIDの複数選択ドロップダウン。
 *
 * 権限管理で、クライアントIDを列に並べると数が増えたとき横に詰む。
 * 行ごとにこのドロップダウンで複数選択する。
 *
 * ドロップダウンは createPortal で body 直下に出し、position: fixed で
 * トリガーの位置に合わせる。テーブルの overflow: auto の中に置くと
 * 枠で切られて選択肢が隠れるため（絶対配置では回避できない）。
 *
 * 「何も選ばない = 全クライアントID可」なので、空のときはその旨を表示する
 * （空欄だと「使えない」と誤解される）。
 */
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { selectStyle } from "./ui.js";

export interface ClientIdOption {
  id: string;
  label: string;
  environment: string;
}

export default function ClientIdMultiSelect({
  options,
  selected,
  disabled,
  onChange,
}: {
  options: ClientIdOption[];
  selected: string[];
  disabled?: boolean;
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // トリガーの画面上の位置にメニューを合わせる
  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // 下に入らなければ上に出す
    const openUp = r.bottom + 280 > window.innerHeight && r.top > 280;
    setPos({
      top: openUp ? r.top - 4 - Math.min(280, options.length * 40 + 48) : r.bottom + 4,
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // 外側クリック・スクロール・リサイズで閉じる／追従する
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false); // 追従より閉じるほうが安全
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const selectedSet = new Set(selected);
  const summary =
    selected.length === 0
      ? "すべてのクライアントID"
      : options
          .filter((o) => selectedSet.has(o.id))
          .map((o) => o.label)
          .join("、") || `${selected.length} 件`;

  const toggle = (id: string) => {
    onChange(selectedSet.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  return (
    <div style={{ width: "100%", minWidth: 200 }}>
      <div
        ref={triggerRef}
        onClick={() => !disabled && setOpen(!open)}
        style={{
          ...selectStyle,
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 36,
        }}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: selected.length === 0 ? "#9a9a96" : "#1a1a1a",
            fontSize: 13,
          }}
        >
          {summary}
        </span>
        <span style={{ fontSize: 10, color: "#999", flexShrink: 0, marginLeft: 8 }}>▼</span>
      </div>

      {open &&
        !disabled &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 1000,
              background: "white",
              border: "1px solid #e8e7e3",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
              maxHeight: 280,
              overflowY: "auto",
            }}
          >
            <div
              onClick={() => onChange([])}
              style={{
                padding: "8px 12px",
                fontSize: 13,
                color: selected.length === 0 ? "#534AB7" : "#6b6b68",
                fontWeight: selected.length === 0 ? 600 : 400,
                cursor: "pointer",
                borderBottom: "1px solid #f0efeb",
              }}
            >
              すべてのクライアントID（絞り込まない）
            </div>
            {options.map((o) => (
              <label
                key={o.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                <input type="checkbox" checked={selectedSet.has(o.id)} onChange={() => toggle(o.id)} />
                <span style={{ flex: 1 }}>{o.label}</span>
                <span style={{ fontSize: 11, color: "#9a9a96" }}>
                  {o.environment === "production" ? "本番" : "sandbox"}
                </span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
